'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { cn } from '@repo/ui/lib/utils';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cva, VariantProps } from 'class-variance-authority';
const Select = SelectPrimitive.Root;
const SelectPortal = SelectPrimitive.Portal;
function SelectContent(t0) {
  const $ = _c(27);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      align: t1,
      alignItemWithTrigger: t2,
      alignOffset: t3,
      children,
      className,
      side: t4,
      sideOffset: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  const align = t1 === undefined ? "center" : t1;
  const alignItemWithTrigger = t2 === undefined ? true : t2;
  const alignOffset = t3 === undefined ? 0 : t3;
  const side = t4 === undefined ? "bottom" : t4;
  const sideOffset = t5 === undefined ? 4 : t5;
  let t6;
  if ($[9] !== className) {
    t6 = cn("relative isolate z-100 max-h-(--available-height) min-w-(--anchor-width) origin-(--transform-origin) overflow-y-auto rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className);
    $[9] = className;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SelectScrollUpButton data-sentry-element="SelectScrollUpButton" data-sentry-source-file="select.tsx" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== children) {
    t8 = <SelectPrimitive.List data-sentry-element="SelectPrimitive.List" data-sentry-source-file="select.tsx">{children}</SelectPrimitive.List>;
    $[12] = children;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SelectScrollDownButton data-sentry-element="SelectScrollDownButton" data-sentry-source-file="select.tsx" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== alignItemWithTrigger || $[16] !== props || $[17] !== t6 || $[18] !== t8) {
    t10 = <SelectPrimitive.Popup className={t6} data-align-trigger={alignItemWithTrigger} data-slot="select-content" {...props} data-sentry-element="SelectPrimitive.Popup" data-sentry-source-file="select.tsx">{t7}{t8}{t9}</SelectPrimitive.Popup>;
    $[15] = alignItemWithTrigger;
    $[16] = props;
    $[17] = t6;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== align || $[21] !== alignItemWithTrigger || $[22] !== alignOffset || $[23] !== side || $[24] !== sideOffset || $[25] !== t10) {
    t11 = <SelectPrimitive.Positioner align={align} alignItemWithTrigger={alignItemWithTrigger} alignOffset={alignOffset} className="isolate z-100" side={side} sideOffset={sideOffset} data-sentry-element="SelectPrimitive.Positioner" data-sentry-component="SelectContent" data-sentry-source-file="select.tsx">{t10}</SelectPrimitive.Positioner>;
    $[20] = align;
    $[21] = alignItemWithTrigger;
    $[22] = alignOffset;
    $[23] = side;
    $[24] = sideOffset;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function SelectGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("scroll-my-1 p-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Group className={t1} data-slot="select-group" {...props} data-sentry-element="SelectPrimitive.Group" data-sentry-component="SelectGroup" data-sentry-source-file="select.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectItem(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children) {
    t2 = <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap" data-sentry-element="SelectPrimitive.ItemText" data-sentry-source-file="select.tsx">{children}</SelectPrimitive.ItemText>;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectPrimitive.ItemIndicator render={t3} data-sentry-element="SelectPrimitive.ItemIndicator" data-sentry-source-file="select.tsx"><CheckIcon className="pointer-events-none" data-sentry-element="CheckIcon" data-sentry-source-file="select.tsx" /></SelectPrimitive.ItemIndicator>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t5 = <SelectPrimitive.Item className={t1} data-slot="select-item" {...props} data-sentry-element="SelectPrimitive.Item" data-sentry-component="SelectItem" data-sentry-source-file="select.tsx">{t2}{t4}</SelectPrimitive.Item>;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function SelectLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-1.5 py-1 text-xs text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.GroupLabel className={t1} data-slot="select-label" {...props} data-sentry-element="SelectPrimitive.GroupLabel" data-sentry-component="SelectLabel" data-sentry-source-file="select.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectScrollDownButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDownIcon data-sentry-element="ChevronDownIcon" data-sentry-source-file="select.tsx" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollDownArrow className={t1} data-slot="select-scroll-down-button" {...props} data-sentry-element="SelectPrimitive.ScrollDownArrow" data-sentry-component="SelectScrollDownButton" data-sentry-source-file="select.tsx">{t2}</SelectPrimitive.ScrollDownArrow>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SelectScrollUpButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUpIcon data-sentry-element="ChevronUpIcon" data-sentry-source-file="select.tsx" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollUpArrow className={t1} data-slot="select-scroll-up-button" {...props} data-sentry-element="SelectPrimitive.ScrollUpArrow" data-sentry-component="SelectScrollUpButton" data-sentry-source-file="select.tsx">{t2}</SelectPrimitive.ScrollUpArrow>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SelectSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("pointer-events-none -mx-1 my-1 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Separator className={t1} data-slot="select-separator" {...props} data-sentry-element="SelectPrimitive.Separator" data-sentry-component="SelectSeparator" data-sentry-source-file="select.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export const selectVariant = cva("flex w-fit items-center justify-between gap-1.5 bg-transparent py-2 pr-1 pl-2.5 text-sm whitespace-nowrap outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground data-[size=default]:h-8 data-[size=sm]:h-7 data-[size=sm]:rounded-[min(var(--radius-md),10px)] *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", {
  defaultVariants: {
    variant: 'default'
  },
  variants: {
    variant: {
      default: 'rounded-md border border-input shadow-xs transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
      ghost: 'rounded-md hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
      'no-style': ''
    }
  }
});
function SelectTrigger(t0) {
  const $ = _c(18);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      children,
      className,
      hideChevron: t1,
      size: t2,
      variant: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const hideChevron = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "default" : t2;
  const variant = t3 === undefined ? "default" : t3;
  let t4;
  if ($[7] !== className || $[8] !== variant) {
    t4 = cn(selectVariant({
      variant
    }), className);
    $[7] = className;
    $[8] = variant;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== hideChevron) {
    t5 = !hideChevron && <SelectPrimitive.Icon render={<ChevronDownIcon className="pointer-events-none size-4" />} />;
    $[10] = hideChevron;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== children || $[13] !== props || $[14] !== size || $[15] !== t4 || $[16] !== t5) {
    t6 = <SelectPrimitive.Trigger className={t4} data-size={size} data-slot="select-trigger" {...props} data-sentry-element="SelectPrimitive.Trigger" data-sentry-component="SelectTrigger" data-sentry-source-file="select.tsx">{children}{t5}</SelectPrimitive.Trigger>;
    $[12] = children;
    $[13] = props;
    $[14] = size;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function SelectValue(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-1 text-left", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Value className={t1} data-slot="select-value" {...props} data-sentry-element="SelectPrimitive.Value" data-sentry-component="SelectValue" data-sentry-source-file="select.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectPortal, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };